import { Attendance, DetailedSignup, Event, Shift, ShiftType, Signup } from '@amzn/red-velvet-api';
import { ShiftStatus } from './volunteerEventShiftsTableTypes';
import { getShiftStatus, toEpochMs } from './volunteerEventShiftsTableUtils';

/**
 * One event the user participates in, with the shifts backing its card. Mirrors WaitlistEventGroup.
 * Deliberately carries no signups/attendances: those are threaded in live (see
 * mapSignupDataByEventId) so a cancel updates the row in place, and holding a frozen copy here would
 * invite resurrecting a cancelled signup's row state.
 */
export interface ActivityEventGroup {
  event: Event;
  shifts: Shift[];
}

/** The user's signup/attendance records for one event, keyed per event by mapSignupDataByEventId. */
export interface ActivityEventSignupData {
  signups: Signup[];
  attendances: Attendance[];
}

/**
 * Benevity's phantom "Waitlist" shift (auto-created when accept_waitlist is on). Signups on it are
 * legacy GiveHub-waitlist artifacts, not real participation — hidden from volunteer views (cce-7639).
 * Exact-name match deliberately mirrors the volunteer event-page filter (no trim — see
 * eventVolunteerShiftTable). DROP_IN and unlisted shifts are NOT excluded here: walk-in and
 * retroactive attendance-only records live on those types and must keep showing on the Past tab.
 */
export function isPhantomWaitlistShift(shift: Shift): boolean {
  return shift.shiftType === ShiftType.WAITLIST || shift.name === 'Waitlist';
}

/**
 * Group drained detailed signups into one ActivityEventGroup per event, dropping phantom-waitlist
 * shifts. Shifts are deduped by shiftId. Insertion order is preserved; ordering for display is
 * applied by the partition step.
 */
export function groupDetailedSignupsByEvent(
  detailedSignups: DetailedSignup[]
): ActivityEventGroup[] {
  const groupsByEventId = new Map<string, ActivityEventGroup>();
  const seenShiftIds = new Set<string>();

  for (const { event, shift } of detailedSignups) {
    if (isPhantomWaitlistShift(shift)) continue;

    let group = groupsByEventId.get(event.eventId);
    if (!group) {
      group = { event, shifts: [] };
      groupsByEventId.set(event.eventId, group);
    }

    if (!seenShiftIds.has(shift.shiftId)) {
      seenShiftIds.add(shift.shiftId);
      group.shifts.push(shift);
    }
  }

  // Events left with no visible shifts (only phantom rows) produce no card.
  return Array.from(groupsByEventId.values()).filter((group) => group.shifts.length > 0);
}

/**
 * The user's live signups/attendances per event, threaded into each (frozen-membership) card so a
 * cancel updates the row in place without removing the card mid-session. Phantom rows are not
 * filtered here — their shifts never render, so their signups are inert.
 */
export function mapSignupDataByEventId(
  detailedSignups: DetailedSignup[]
): Map<string, ActivityEventSignupData> {
  const byEventId = new Map<string, ActivityEventSignupData>();

  for (const { event, signupDetails } of detailedSignups) {
    let entry = byEventId.get(event.eventId);
    if (!entry) {
      entry = { signups: [], attendances: [] };
      byEventId.set(event.eventId, entry);
    }
    if (signupDetails.signup) entry.signups.push(signupDetails.signup);
    if (signupDetails.attendance) entry.attendances.push(signupDetails.attendance);
  }

  return byEventId;
}

/**
 * Restore the spot a volunteer vacates when they cancel, for the Volunteer Activity table.
 * - Card shifts are frozen at first drain so a cancel doesn't yank the row (frozen membership).
 * - But signupsRemaining was captured while the volunteer still held a spot, so after a cancel the
 *   frozen shift still counts them: a full shift (e.g. 1/1) reads full and getShiftAction offers
 *   Join waitlist instead of Sign up. The cancelled-off shift is also gone from the live drain, so
 *   there is no fresh shift to overlay.
 * - Fix: for a frozen shift the volunteer no longer participates in (no live signup or attendance),
 *   give the one vacated spot back so it is not treated as full for their own re-signup.
 * - Shifts still participated in are returned unchanged (identity preserved so the table's frozen
 *   row order and row memoization are undisturbed).
 */
export function restoreCancelledShiftCapacity(
  shifts: Shift[],
  liveData: ActivityEventSignupData | undefined
): Shift[] {
  const participatingShiftIds = new Set<string>([
    ...(liveData?.signups ?? []).map((signup) => signup.shiftId),
    ...(liveData?.attendances ?? []).map((attendance) => attendance.shiftId),
  ]);
  return shifts.map((shift) =>
    participatingShiftIds.has(shift.shiftId)
      ? shift
      : { ...shift, signupsRemaining: (shift.signupsRemaining ?? 0) + 1 }
  );
}

/**
 * Past only when ALL of the user's shifts for the event have ended. IN_PROGRESS is not PAST, so a
 * started-but-unfinished shift keeps the card on Upcoming (classification parity with the Waitlists
 * tab's isPastWaitlistEventGroup).
 */
export function isPastActivityEventGroup(group: ActivityEventGroup): boolean {
  return group.shifts.every((shift) => getShiftStatus(shift) === ShiftStatus.PAST);
}

/** Sort key for Upcoming: the event's earliest not-yet-ended signed-up shift. Missing/invalid start
 *  dates fall to the tail (POSITIVE_INFINITY), matching the table's date comparator convention. */
function earliestUpcomingShiftStart(group: ActivityEventGroup): number {
  const upcoming = group.shifts.filter((shift) => getShiftStatus(shift) !== ShiftStatus.PAST);
  return Math.min(
    ...upcoming.map((shift) => toEpochMs(shift.startDateTime) ?? Number.POSITIVE_INFINITY)
  );
}

/** Sort key for Past (descending): the event's most recent signed-up shift. Missing/invalid start
 *  dates fall to the tail of the newest-first list (NEGATIVE_INFINITY under Math.max). */
function latestShiftStart(group: ActivityEventGroup): number {
  return Math.max(
    ...group.shifts.map((shift) => toEpochMs(shift.startDateTime) ?? Number.NEGATIVE_INFINITY)
  );
}

/**
 * Partition the drained signups into the two tabs' card lists:
 * - upcomingGroups: ascending by earliest upcoming signed-up shift (soonest first)
 * - pastGroups: descending by most recent signed-up shift (newest first)
 * Matches the per-tab ordering the flat SignupList shipped with (oldest-first / newest-first).
 */
export function partitionActivityEventGroups(detailedSignups: DetailedSignup[]): {
  upcomingGroups: ActivityEventGroup[];
  pastGroups: ActivityEventGroup[];
} {
  const groups = groupDetailedSignupsByEvent(detailedSignups);
  const upcomingGroups: ActivityEventGroup[] = [];
  const pastGroups: ActivityEventGroup[] = [];

  for (const group of groups) {
    (isPastActivityEventGroup(group) ? pastGroups : upcomingGroups).push(group);
  }

  upcomingGroups.sort((a, b) => earliestUpcomingShiftStart(a) - earliestUpcomingShiftStart(b));
  pastGroups.sort((a, b) => latestShiftStart(b) - latestShiftStart(a));

  return { upcomingGroups, pastGroups };
}

/**
 * Move the group containing shiftId to the front (one-click cancel deep link: the target card must
 * render without paging so its row's auto-cancel effect mounts). No-op when no group matches.
 */
export function pinGroupWithShiftFirst(
  groups: ActivityEventGroup[],
  shiftId: string | undefined
): ActivityEventGroup[] {
  if (!shiftId) return groups;
  const index = groups.findIndex((group) => group.shifts.some((s) => s.shiftId === shiftId));
  if (index <= 0) return groups;
  return [groups[index], ...groups.slice(0, index), ...groups.slice(index + 1)];
}
